import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import Logo from '../components/Logo'
import Page from '../components/Page'

const LAST_MODE_PREFIX = 'fp_last_spin_mode_'
const SKIP_GATE_KEY = 'flickpick_skip_gate'

function lastModeKey(userId) {
  return `${LAST_MODE_PREFIX}${userId}`
}

export default function SpinMode() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [skipNext, setSkipNext] = useState(false)

  const lastChoice = useMemo(() => {
    if (!user) return null
    try {
      const saved = localStorage.getItem(lastModeKey(user.id))
      if (saved === 'solo' || saved === 'group') return saved
    } catch {
      // localStorage unavailable — fall through
    }
    return null
  }, [user])

  function choose(mode) {
    if (user) {
      try { localStorage.setItem(lastModeKey(user.id), mode) } catch { /* ignore */ }
    }
    try {
      localStorage.setItem(SKIP_GATE_KEY, skipNext ? 'true' : 'false')
    } catch { /* ignore */ }
    navigate(mode === 'solo' ? '/solo' : '/')
  }

  return (
    <Page mode="cinema">
      <div className="w-full max-w-lg flex flex-col items-center">
        <div className="mb-10">
          <Logo size="md" />
        </div>

        <h1 className="fp-display text-4xl sm:text-5xl text-center mb-3 text-white">
          Who's watching tonight?
        </h1>
        <p className="text-base text-[var(--fp-text-muted)] text-center mb-12 max-w-md">
          One answer sends you to the wheel. The other gets your crew involved.
        </p>

        <div className="w-full flex flex-col sm:flex-row gap-5">
          <ModeCard
            tone="gold"
            title="Just me"
            tagline="Pick from your watchlist. Spin solo."
            icon={<SoloIcon />}
            isLast={lastChoice === 'solo'}
            onClick={() => choose('solo')}
          />
          <ModeCard
            tone="red"
            title="My crew"
            tagline="Rally the group. Everyone picks."
            icon={<GroupIcon />}
            isLast={lastChoice === 'group'}
            onClick={() => choose('group')}
          />
        </div>

        <label className="flex items-center gap-3 mt-8 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={skipNext}
            onChange={e => setSkipNext(e.target.checked)}
            className="w-5 h-5 cursor-pointer"
            style={{ accentColor: 'var(--fp-red)' }}
          />
          <span className="text-sm text-[var(--fp-text-muted)]">
            Skip this next time
          </span>
        </label>

        <button
          onClick={() => navigate('/group')}
          className="mt-4 text-xs text-[var(--fp-text-dim)] bg-transparent border-none cursor-pointer hover:text-[var(--fp-red)] transition-colors underline-offset-4 hover:underline"
        >
          Change this later in Settings
        </button>
      </div>
    </Page>
  )
}

function ModeCard({ tone, title, tagline, icon, isLast, onClick }) {
  const accent = tone === 'gold' ? 'var(--fp-gold)' : 'var(--fp-red)'
  const glow = tone === 'gold'
    ? '0 12px 44px rgba(212, 175, 55, 0.18)'
    : '0 12px 44px var(--fp-red-glow)'

  return (
    <button
      onClick={onClick}
      className="fp-card-elevated text-left cursor-pointer w-full relative border-none transition-all hover:-translate-y-0.5"
      style={{
        boxShadow: glow,
        outline: isLast ? `1.5px solid ${accent}` : 'none',
        outlineOffset: isLast ? '-1px' : '0',
        minHeight: '44px',
      }}
    >
      {isLast && (
        <span
          className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.18em] font-semibold px-2.5 py-1 rounded-full"
          style={{
            background: tone === 'gold' ? 'var(--fp-gold-soft)' : 'var(--fp-red-glow)',
            color: accent,
          }}
        >
          Last time
        </span>
      )}
      <div className="flex items-center gap-5">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
          style={{
            background: tone === 'gold' ? 'var(--fp-gold-soft)' : 'var(--fp-red-glow)',
            color: accent,
          }}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="fp-display text-2xl text-white mb-1">{title}</div>
          <div className="text-sm text-[var(--fp-text-muted)]">{tagline}</div>
        </div>
      </div>
    </button>
  )
}

function SoloIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  )
}

function GroupIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" />
      <path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  )
}
